import { BrandLogo } from "@/components/shared/BrandLogo";
import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import {
  Search, AlertTriangle, CheckCircle, MapPin, Gift, Plus,
  X, Loader2, Shield, Tag, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const ConfidenceBadge = ({ score }) => {
  const color = score >= 85 ? "bg-red-100 text-red-700 border-red-200"
    : score >= 70 ? "bg-amber-100 text-amber-700 border-amber-200"
    : "bg-sky-100 text-sky-700 border-sky-200";
  const label = score >= 85 ? "Very likely match" : score >= 70 ? "Possible match" : "Weak match";
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${color}`} data-testid="confidence-badge">
      {score}% — {label}
    </span>
  );
};

const MatchCard = ({ match, onAddAlias }) => {
  const [showAliases, setShowAliases] = useState(false);
  const [newAlias, setNewAlias] = useState("");
  const [adding, setAdding] = useState(false);

  const handleAddAlias = async () => {
    if (!newAlias.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/aliases`, {
        business_id: match.business_id,
        alias: newAlias.trim(),
      });
      toast.success(`Alias "${newAlias}" added`);
      setNewAlias("");
      onAddAlias?.();
    } catch {
      toast.error("Failed to add alias");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      className={`rounded-xl border p-3 space-y-2 ${
        match.confidence >= 85 ? "bg-red-50/50 border-red-200" :
        match.confidence >= 70 ? "bg-amber-50/50 border-amber-200" :
        "bg-sky-50/50 border-sky-200"
      }`}
      data-testid={`match-card-${match.business_id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-white border border-slate-100 flex items-center justify-center">
          <BrandLogo name={match.name} logoUrl={match.logo_url} size="sm" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800">{match.name}</p>
          <p className="text-[10px] text-slate-400">{match.category} · {match.coverage || match.city}</p>
        </div>
        <ConfidenceBadge score={match.confidence} />
      </div>

      {/* Match type */}
      {match.match_type === "alias" && match.matched_alias && (
        <div className="flex items-center gap-1.5 text-[10px] text-violet-600 bg-violet-50 rounded-lg px-2.5 py-1">
          <Tag className="w-3 h-3" />
          Matched via alias: "{match.matched_alias}"
        </div>
      )}

      {/* Offer title */}
      {match.offer_title && (
        <p className="text-xs text-slate-500 line-clamp-1">{match.offer_title}</p>
      )}

      {/* Addresses for verification */}
      {match.addresses?.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold text-slate-500">Addresses for verification:</p>
          {match.addresses.map((addr, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[10px] text-slate-600 bg-white rounded-lg px-2 py-1 border border-slate-100">
              <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="truncate">{addr}</span>
            </div>
          ))}
        </div>
      )}
      {match.addresses?.length === 0 && (
        <p className="text-[10px] text-slate-400 italic">No specific address on file — {match.coverage || "coverage unknown"}</p>
      )}

      {/* Existing aliases */}
      {match.aliases?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          <span className="text-[10px] text-slate-400">Known aliases:</span>
          {match.aliases.map((a, i) => (
            <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{a}</span>
          ))}
        </div>
      )}

      {/* Add alias toggle */}
      <button
        onClick={() => setShowAliases(!showAliases)}
        className="flex items-center gap-1 text-[10px] font-semibold text-indigo-600 hover:text-indigo-700"
        data-testid={`toggle-alias-${match.business_id}`}
      >
        {showAliases ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Add alias for this business
      </button>

      {showAliases && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newAlias}
            onChange={(e) => setNewAlias(e.target.value)}
            placeholder="e.g., Beni Hana, Benihana's"
            className="flex-1 text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            data-testid={`alias-input-${match.business_id}`}
          />
          <button
            onClick={handleAddAlias}
            disabled={adding || !newAlias.trim()}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50"
            data-testid={`alias-save-${match.business_id}`}
          >
            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Save
          </button>
        </div>
      )}
    </div>
  );
};

export default function DuplicateCheckPanel() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const checkDuplicate = useCallback(async (name) => {
    if (!name || name.length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const payload = { name };
      if (category) payload.category = category;
      if (city) payload.city = city;
      const res = await axios.post(`${API}/admin/check-duplicate`, payload);
      setResults(res.data);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [category, city]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults(null);
      return;
    }
    const timer = setTimeout(() => checkDuplicate(query), 500);
    return () => clearTimeout(timer);
  }, [query, checkDuplicate]);

  return (
    <div className="space-y-3" data-testid="duplicate-check-panel">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Try "Benihana", "DQ", or "Starbucks Coffee"...'
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
          data-testid="dupe-check-input"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
      </div>

      {/* Optional filters */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="text-[10px] font-semibold text-slate-400 hover:text-slate-600 flex items-center gap-1"
      >
        {showFilters ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Narrow by category/city
      </button>
      {showFilters && (
        <div className="flex gap-2">
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Category (e.g., Restaurant)"
            className="flex-1 text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            data-testid="dupe-category-input"
          />
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City (e.g., Sacramento)"
            className="flex-1 text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            data-testid="dupe-city-input"
          />
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-2">
          {results.has_potential_duplicates ? (
            <>
              <div className="flex items-center gap-2 px-1">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-semibold text-amber-700">
                  {results.matches.length} potential match{results.matches.length !== 1 ? "es" : ""} found
                </span>
              </div>
              <p className="text-[10px] text-slate-400 px-1">
                Did you mean one of these? Verify by checking the addresses below.
              </p>
              {results.matches.map((m) => (
                <MatchCard key={m.business_id} match={m} onAddAlias={() => checkDuplicate(query)} />
              ))}
            </>
          ) : query.length >= 2 ? (
            <div className="flex items-center gap-2 px-1 py-3">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-semibold text-emerald-700">No duplicates found for "{query}"</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
